import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { downloadZip } from 'client-zip';
import { appContext } from './AppContext';
import { getImagesPath } from '../utils/authenticated-requests/images';
import { generateTimestamp } from '../utils/helpers';
import {
  DOWNLOAD_IMAGES_TAB_PARAM,
  DOWNLOAD_IMAGES_TABS,
  IMAGE_DOWNLOAD_SLOT_COUNT,
} from '../Components/Pages/DownloadImages/constants';
import {
  createImageDownloadFailure,
  getErrorDetails,
  getInitialDownloadProgress,
  getImageFileName,
} from '../Components/Pages/DownloadImages/helpers';
import {
  ActiveImageDownload,
  DownloadZipEntry,
  ImageDownloadFailure,
  ImageDownloadItem,
  UseImageDownloadResult,
} from '../Components/Pages/DownloadImages/interfaces';
import {
  ImageDownloadAttempt,
  ImageDownloadProgress,
} from '../Components/Pages/DownloadImages/types';
import { useTranslation } from 'react-i18next';
import { useAppAlert } from './AppAlertContext';

const ImageDownloadContext = createContext<UseImageDownloadResult | undefined>(
  undefined,
);

/**
 * Provides the image download context to its child components.
 *
 * @param children The props containing the children elements.
 * @returns The provider component wrapping its children with the image download context.
 */
export const ImageDownloadProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const { t } = useTranslation();
  const { activeProject, company } = useContext(appContext);
  const { addAppAlert, removeAppAlert } = useAppAlert();
  const activeDownloadRef = useRef<ActiveImageDownload | null>(null);
  const downloadAlertIdRef = useRef<string | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const activeProjectIdRef = useRef<string | undefined>(activeProject.id);
  const companyRef = useRef<string>(company);

  const [downloadProgress, setDownloadProgress] =
    useState<ImageDownloadProgress>({});
  const [downloadFailures, setDownloadFailures] = useState<
    ImageDownloadFailure[]
  >([]);
  const [fatalDownloadError, setFatalDownloadError] = useState<string | null>(
    null,
  );
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [wasDownloadCancelled, setWasDownloadCancelled] =
    useState<boolean>(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);
  const [activeImageDownloadList, setActiveImageDownloadList] = useState<
    ImageDownloadItem[]
  >([]);

  const downloadTabLink = `/${company}/projects/${activeProject.id}/download-images?${DOWNLOAD_IMAGES_TAB_PARAM}=${DOWNLOAD_IMAGES_TABS.download}`;

  /**
   * Determines whether a download should stop processing.
   *
   * A download is cancelled when its AbortController has been aborted or when
   * another download has replaced it as the active download.
   *
   * @param activeDownload The download to check.
   * @returns True when the download is aborted or stale.
   */
  const isDownloadCancelled = (activeDownload: ActiveImageDownload): boolean =>
    activeDownload.controller.signal.aborted ||
    activeDownloadRef.current !== activeDownload;

  /** Clears state describing the previous download outcome. */
  const clearDownloadOutcome = (): void => {
    setDownloadFailures([]);
    setFatalDownloadError(null);
    setWasDownloadCancelled(false);
    setIsCancelDialogOpen(false);
  };

  /**
   * Update progress for an image download.
   *
   * @param activeDownload The download to update.
   * @param drillholeId The associated drillhole ID for the image.
   * @param succeeded Whether the processed image was downloaded successfully.
   */
  const updateProgress = ({
    activeDownload,
    drillholeId,
    succeeded,
  }: {
    activeDownload: ActiveImageDownload;
    drillholeId: string;
    succeeded: boolean;
  }): void => {
    if (isDownloadCancelled(activeDownload)) {
      return;
    }

    // Update the progress state for the specified drillhole
    // or initialize it if it doesn't exist yet
    setDownloadProgress((previousProgress) => {
      const currentProgress = previousProgress[drillholeId] ?? {
        processed: 0,
        succeeded: 0,
        failed: 0,
      };

      return {
        ...previousProgress,
        [drillholeId]: {
          processed: currentProgress.processed + 1,
          succeeded: currentProgress.succeeded + (succeeded ? 1 : 0),
          failed: currentProgress.failed + (succeeded ? 0 : 1),
        },
      };
    });
  };

  /**
   * Downloads one image by resolving its signed URL and reading the response
   * body into a ZIP entry.
   *
   * Errors are converted into image-specific failure results so other images
   * in the shared download queue can continue downloading independently.
   *
   * @param drillholeId The ID of the drillhole containing the image.
   * @param image The image metadata and source path.
   * @param activeDownload The active download used for cancellation checks.
   * @returns A successful ZIP entry, a failure record, or a cancelled result.
   */
  const downloadImage = async (
    drillholeId: string,
    image: ImageDownloadItem['images'][number],
    activeDownload: ActiveImageDownload,
  ): Promise<ImageDownloadAttempt> => {
    if (isDownloadCancelled(activeDownload)) {
      return { status: 'cancelled' };
    }

    if (!image.path) {
      return {
        status: 'failed',
        failure: createImageDownloadFailure(
          image,
          drillholeId,
          'DOWNLOAD_IMAGES_IMAGE_PATH_MISSING',
        ),
      };
    }

    // Get the signed URL for the image from S3
    // This is a separate request from the actual image download
    let signedUrl: string;

    try {
      const response = await getImagesPath(image.path);
      if (isDownloadCancelled(activeDownload)) {
        return { status: 'cancelled' };
      }
      signedUrl = response.result;
    } catch (error) {
      if (isDownloadCancelled(activeDownload)) {
        return { status: 'cancelled' };
      }
      const errorDetails = getErrorDetails(error);

      return {
        status: 'failed',
        failure: createImageDownloadFailure(
          image,
          drillholeId,
          errorDetails.message,
          errorDetails.httpStatus,
        ),
      };
    }

    // Fetch the actual image using the signed URL obtained from S3
    let imageResponse: Response;

    try {
      imageResponse = await fetch(signedUrl, {
        signal: activeDownload.controller.signal,
      });
    } catch (error) {
      if (isDownloadCancelled(activeDownload)) {
        return { status: 'cancelled' };
      }
      const errorDetails = getErrorDetails(error);
      return {
        status: 'failed',
        failure: createImageDownloadFailure(
          image,
          drillholeId,
          errorDetails.message,
          errorDetails.httpStatus,
        ),
      };
    }

    if (!imageResponse.ok) {
      return {
        status: 'failed',
        failure: createImageDownloadFailure(
          image,
          drillholeId,
          'DOWNLOAD_IMAGES_IMAGE_REQUEST_FAILED',
          imageResponse.status,
        ),
      };
    }

    // Convert the fetched image response into a Blob
    // for the ZIP archive
    try {
      return {
        status: 'succeeded',
        entry: {
          name: getImageFileName(
            activeProject?.company ?? '',
            image,
            activeDownload.timeStamp,
          ),
          input: await imageResponse.blob(),
        },
      };
    } catch (error) {
      if (isDownloadCancelled(activeDownload)) {
        return { status: 'cancelled' };
      }
      const errorDetails = getErrorDetails(error);
      return {
        status: 'failed',
        failure: createImageDownloadFailure(
          image,
          drillholeId,
          errorDetails.message,
          errorDetails.httpStatus,
        ),
      };
    }
  };

  /**
   * Streams ZIP entries into a Blob while allowing the stream to be aborted.
   *
   * @param entries Successful image entries to include in the archive.
   * @param activeDownload The active download controlling stream cancellation.
   * @returns The ZIP Blob or null when the download is aborted or no longer active.
   * @throws If the ZIP response does not expose a readable body.
   */
  const createZipBlob = async (
    entries: DownloadZipEntry[],
    activeDownload: ActiveImageDownload,
  ): Promise<Blob | null> => {
    const response = downloadZip(entries);
    const reader = response.body?.getReader();

    if (!reader) {
      throw new Error('Unable to read image archive stream');
    }

    const chunks: ArrayBuffer[] = [];

    // Intentionally returns void to avoid unhandled promise rejection
    // when the download is cancelled
    const cancelReader = (): void => {
      void reader.cancel().catch(() => undefined);
    };

    activeDownload.controller.signal.addEventListener('abort', cancelReader, {
      once: true,
    });

    try {
      while (!isDownloadCancelled(activeDownload)) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        const chunkBuffer = new ArrayBuffer(value.byteLength);

        new Uint8Array(chunkBuffer).set(value);

        chunks.push(chunkBuffer);
      }

      if (isDownloadCancelled(activeDownload)) {
        return null;
      }

      return new Blob(chunks, {
        type: 'application/zip',
      });
    } finally {
      activeDownload.controller.signal.removeEventListener(
        'abort',
        cancelReader,
      );
      reader.releaseLock();
    }
  };

  /**
   * Starts a download for the selected images.
   *
   * All selected images share one queue with a max number of concurrent download slots.
   * Each download slot claims the next available image after completing its previous attempt.
   *
   * Successful image entries are combined into a ZIP archive and downloaded by
   * the browser. Individual image failures do not stop the remaining queue.
   *
   * @param imageDownloadList Drillholes and images selected for download.
   */
  const startDownload = async (
    imageDownloadList: ImageDownloadItem[],
  ): Promise<void> => {
    if (activeDownloadRef.current) {
      // A download is already in progress; ignore this request
      return;
    }

    const activeDownload: ActiveImageDownload = {
      controller: new AbortController(),
      timeStamp: generateTimestamp(),
      imageDownloadList,
    };
    activeDownloadRef.current = activeDownload;

    let objectUrl: string | undefined;
    setDownloadProgress(getInitialDownloadProgress(imageDownloadList));
    setActiveImageDownloadList(imageDownloadList);
    clearDownloadOutcome();
    setIsDownloading(true);

    const downloadAlertId = addAppAlert({
      severity: 'info',
      message: t('DOWNLOAD_IMAGES_DOWNLOAD_IN_PROGRESS'),
      link: downloadTabLink,
      shouldCloseOnClickAway: false,
      progressMode: 'indeterminate',
      shouldAutoHide: false,
    });
    downloadAlertIdRef.current = downloadAlertId;

    try {
      const imageQueue = imageDownloadList.flatMap((item) =>
        item.images.map((image) => ({ drillholeId: item.id, image })),
      );
      const entriesByImage: Array<DownloadZipEntry | undefined> = Array.from({
        length: imageQueue.length,
      });

      /**
       * Claims and downloads the next available image for a download slot.
       *
       * Images are claimed through the shared index so download slots do not
       * process the same item twice. Each slot claims another image
       * immediately after its previous attempt completes, regardless of its
       * drillhole.
       */
      let nextImageIndex = 0;
      const downloadNextImage = async (): Promise<void> => {
        while (
          !isDownloadCancelled(activeDownload) &&
          nextImageIndex < imageQueue.length
        ) {
          const imageIndex = nextImageIndex;
          nextImageIndex += 1;
          const { drillholeId, image } = imageQueue[imageIndex];

          const result = await downloadImage(
            drillholeId,
            image,
            activeDownload,
          );

          if (result.status === 'cancelled') {
            break;
          }

          if (result.status === 'succeeded') {
            entriesByImage[imageIndex] = result.entry;
            updateProgress({
              activeDownload,
              drillholeId,
              succeeded: true,
            });
          } else if (!isDownloadCancelled(activeDownload)) {
            setDownloadFailures((previousFailures) => [
              ...previousFailures,
              result.failure,
            ]);
            updateProgress({
              activeDownload,
              drillholeId,
              succeeded: false,
            });
          }
        }
      };

      const downloadSlotCount = Math.min(
        IMAGE_DOWNLOAD_SLOT_COUNT,
        imageQueue.length,
      );

      // Start downloading images using the available download slots
      await Promise.all(
        Array.from({ length: downloadSlotCount }, downloadNextImage),
      );

      if (isDownloadCancelled(activeDownload)) {
        return;
      }

      const zipEntries = entriesByImage.filter(
        (entry): entry is DownloadZipEntry => entry !== undefined,
      );

      if (zipEntries.length === 0) {
        // No successful images to include in the ZIP; skip creating the archive
        if (imageQueue.length > 0) {
          addAppAlert({
            severity: 'error',
            message: t('DOWNLOAD_IMAGES_ALERT_ALL_FAILED'),
            link: downloadTabLink,
            shouldCloseOnClickAway: false,
          });
        }
        return;
      }

      const blob = await createZipBlob(zipEntries, activeDownload);

      if (!blob || isDownloadCancelled(activeDownload)) {
        // Failed to create the ZIP blob or download was cancelled
        return;
      }

      // Create an object URL for the ZIP blob for the browser to download
      objectUrl = URL.createObjectURL(blob);
      if (isDownloadCancelled(activeDownload)) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = undefined;
        return;
      }

      const link = document.createElement('a');
      link.href = objectUrl;
      // eg "goldspot_images_09.02.2026_10.42.zip"
      link.download = `${activeProject?.company ?? ''}_images_${activeDownload.timeStamp}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      const failedImageCount = imageQueue.length - zipEntries.length;

      if (failedImageCount > 0) {
        addAppAlert({
          severity: 'warning',
          message: t('DOWNLOAD_IMAGES_ALERT_PARTIAL_SUCCESS', {
            count: failedImageCount,
          }),
          link: downloadTabLink,
          shouldCloseOnClickAway: false,
        });
      } else {
        addAppAlert({
          severity: 'success',
          message: t('DOWNLOAD_IMAGES_ALERT_SUCCESS'),
        });
      }

      // Handle partial and complete success
    } catch (error) {
      if (!isDownloadCancelled(activeDownload)) {
        const { message } = getErrorDetails(error);
        setFatalDownloadError(message);
      }
    } finally {
      if (downloadAlertIdRef.current === downloadAlertId) {
        removeAppAlert(downloadAlertId);
        downloadAlertIdRef.current = null;
      }

      if (activeDownloadRef.current === activeDownload) {
        activeDownloadRef.current = null;
        if (isMountedRef.current) {
          setWasDownloadCancelled(activeDownload.controller.signal.aborted);
          setIsDownloading(false);
        }
      }

      // Using timeout to revoke the object URL after the current call stack
      // or else the object might get removed before the download completes
      setTimeout(() => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      }, 0);
    }
  };

  /** Opens the confirmation dialog for cancelling the active download. */
  const openCancelDialog = () => {
    setIsCancelDialogOpen(true);
  };

  /** Closes the download-cancellation confirmation dialog. */
  const closeCancelDialog = () => {
    setIsCancelDialogOpen(false);
  };

  /**
   * Aborts the active download and resets its visible progress.
   *
   * @param imageDownloadList The current list used to recreate zeroed progress.
   */
  const cancelDownload = (
    imageDownloadList = activeDownloadRef.current?.imageDownloadList ?? [],
  ) => {
    const activeDownload = activeDownloadRef.current;
    if (!activeDownload || activeDownload.controller.signal.aborted) {
      setIsCancelDialogOpen(false);
      // No active download to cancel; ignore this request
      return;
    }

    setIsCancelDialogOpen(false);
    activeDownload.controller.abort();
    setDownloadProgress(getInitialDownloadProgress(imageDownloadList));
    setWasDownloadCancelled(true);
  };

  /** Clears progress, alerts, and dialog state without aborting work. */
  const resetDownloadState = (): void => {
    setDownloadProgress({});
    setActiveImageDownloadList([]);
    clearDownloadOutcome();
  };

  /** Cancel the download if the active project or company changes */
  useEffect(() => {
    const previousProjectId = activeProjectIdRef.current;
    const previousCompany = companyRef.current;
    activeProjectIdRef.current = activeProject.id;
    companyRef.current = company;

    if (
      (previousProjectId !== undefined &&
        previousProjectId !== activeProject.id) ||
      previousCompany !== company
    ) {
      const activeDownload = activeDownloadRef.current;

      if (activeDownload) {
        cancelDownload(activeDownload.imageDownloadList);
      }
    }
  }, [activeProject.id, company]);

  /** Cancel active downloads and clean up their alerts when the provider unmounts */
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      const downloadAlertId = downloadAlertIdRef.current;
      if (downloadAlertId) {
        removeAppAlert(downloadAlertId);
        downloadAlertIdRef.current = null;
      }
      const activeDownload = activeDownloadRef.current;
      if (activeDownload) {
        activeDownload.controller.abort();
        activeDownloadRef.current = null;
      }
    };
  }, [removeAppAlert]);

  return (
    <ImageDownloadContext.Provider
      value={{
        downloadProgress,
        downloadFailures,
        fatalDownloadError,
        isDownloading,
        wasDownloadCancelled,
        isCancelDialogOpen,
        activeImageDownloadList,
        startDownload,
        openCancelDialog,
        closeCancelDialog,
        cancelDownload,
        resetDownloadState,
      }}
    >
      {children}
    </ImageDownloadContext.Provider>
  );
};

/**
 * Manages image-download lifecycle state, progress reporting, ZIP creation,
 * cancellation, and cleanup for the Download Images flow.
 *
 * @returns Download state and handlers for starting, cancelling, and managing
 * the cancellation dialog.
 */
export const useImageDownload = (): UseImageDownloadResult => {
  const context = useContext(ImageDownloadContext);

  if (!context) {
    throw new Error(
      'useImageDownload must be used within ImageDownloadProvider',
    );
  }

  return context;
};
